import type {
  Actor,
  Affiliation,
  ChangeInput,
  ManagedBy,
  MembershipType,
  SetupChange,
  MembershipRecord,
} from './types';

/**
 * Words of the tenant administration screens (Tenant-Administration-Flow.md
 * sections 2-10). Plain language; nothing about other organisations.
 */

export const MANAGED_BY_LABEL: Record<ManagedBy, string> = {
  ASI: 'PartnershipNet',
  ORGANISATION: 'The organisation',
};

export const MANAGED_BY_HELP: Record<ManagedBy, string> = {
  ASI: 'PartnershipNet staff invite and manage the organisation’s users; a Super Admin approves.',
  ORGANISATION:
    'The organisation’s administrators invite and manage their users. Needs an administrator: an active one, or one named in this change.',
};

/** The affiliation label (TA27, TA30): shown to the acting admin only, never stored. */
export const AFFILIATION_LABEL: Record<Affiliation, string> = {
  own: 'Own organisation',
  external: 'External',
  normal: 'Normal account',
};

export const AFFILIATION_HELP: Record<Affiliation, string> = {
  own: 'The e-mail domain belongs to this organisation.',
  external:
    'The e-mail domain belongs to another organisation: the person keeps their own account there.',
  normal: 'A public or unregistered e-mail domain.',
};

export const MEMBERSHIP_TYPE_LABEL: Record<MembershipType, string> = {
  member: 'Member',
  external: 'External',
};

export const MEMBERSHIP_STATE_LABEL: Record<string, string> = {
  requested: 'Waits for PartnershipNet',
  awaiting_organisation: 'Waits for the organisation',
  invited: 'Invited',
  active: 'Active',
  ending_soon: 'Ending soon',
  expired: 'Expired',
  ended: 'Ended',
  rejected: 'Rejected',
  cancelled: 'Cancelled',
  declined: 'Declined',
  accepted: 'Accepted',
  applied: 'Applied',
};

export const CHANGE_STATE_LABEL: Record<string, string> = {
  awaiting_internal: 'Waits for a Super Admin',
  notice: 'Notice to the organisation',
  applied: 'Applied',
  rejected: 'Rejected',
  expired: 'Expired',
  cancelled: 'Cancelled',
  failed: 'Not applied',
};

/** The steps of an invitation (POST invitations/check `path`). */
export const PATH_STEP_LABEL: Record<string, string> = {
  internal_approval: 'a Super Admin',
  organisation_approval: 'an administrator of the organisation',
  invitation: 'the invitation e-mail',
};

/** Why a role cannot be invited now (invitable-roles `reason`), in plain words. */
export const NOT_INVITABLE_TEXT: Record<string, string> = {
  organisation_managed_by_asi: 'PartnershipNet manages this organisation’s administrators',
  organisation_admin_role_required: 'your role cannot invite with it',
  role_outside_packages: 'it holds features outside the organisation’s packages',
  permission_not_delegable: 'it holds rights an organisation cannot give',
};

export const notInvitableText = (reason: string | null | undefined): string =>
  NOT_INVITABLE_TEXT[String(reason ?? '')] ?? 'it cannot be given by invitation now';

/** "Approval needed first: a Super Admin. The invitation e-mail goes out after that." */
export function pathText(path: string[] | undefined): string {
  const steps = (path ?? []).filter((s) => s !== 'invitation');
  if (steps.length === 0) return 'The invitation e-mail goes out at once.';
  const who = steps.map((s) => PATH_STEP_LABEL[s] ?? s);
  return `Approval needed first: ${who.join(', then ')}. The invitation e-mail goes out after that.`;
}

/** 'YYYY-MM-DD' of a 'YYYY-MM-DD HH:MM:SS' answer. */
export const dateOnly = (s: string | null | undefined): string => (s ? String(s).slice(0, 10) : '');

/** Today (UTC) plus whole months, as 'YYYY-MM-DD' (the date input's value). */
export function monthsFromToday(months: number, now: Date = new Date()): string {
  const d = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate()));
  const day = d.getUTCDate();
  d.setUTCMonth(d.getUTCMonth() + months);
  // 31 March + 1 month: the last day of April, not 1 May
  if (d.getUTCDate() !== day) d.setUTCDate(0);
  return d.toISOString().slice(0, 10);
}

/** The longest membership (TA9): 12 months. */
export const MAX_MONTHS = 12;

export const defaultValidUntil = (now?: Date): string => monthsFromToday(MAX_MONTHS, now);

export const todayText = (now: Date = new Date()): string => now.toISOString().slice(0, 10);

/**
 * The earliest end date (UTC), the date inputs' `min`: tomorrow. The server stores a date as its
 * midnight and refuses an end that is not in the future, so today would be refused.
 */
export const tomorrowText = (now: Date = new Date()): string =>
  new Date(now.getTime() + 86_400_000).toISOString().slice(0, 10);

/** An end date error, or null: required, not in the past, at most 12 months away. */
export function validUntilError(value: string, now?: Date): string | null {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(value)) return 'Choose an end date.';
  if (value <= todayText(now)) return 'The end date must be after today.';
  if (value > defaultValidUntil(now)) return 'A membership can last at most 12 months.';
  return null;
}

/** Whole days from now until a 'YYYY-MM-DD HH:MM:SS' (UTC) time; negative when past. */
export function daysUntil(at: string | null | undefined, now: Date = new Date()): number | null {
  if (!at) return null;
  const t = Date.parse(`${String(at).replace(' ', 'T')}Z`);
  if (Number.isNaN(t)) return null;
  return Math.ceil((t - now.getTime()) / 86_400_000);
}

/** "Expires on 29 Oct 2026 (in 14 days)" / "Expired on ..." for a step's end. */
export function expiryText(at: string | null | undefined, now?: Date): string {
  if (!at) return '';
  const days = daysUntil(at, now);
  const date = dateText(at);
  if (days === null) return `Expires on ${date}`;
  if (days < 0) return `Expired on ${date}`;
  if (days === 0) return `Expires today (${date})`;
  return `Expires on ${date} (in ${days} day${days === 1 ? '' : 's'})`;
}

/** Who acted, in words. */
export function actorText(actor: Actor | undefined): string {
  if (!actor) return '';
  if ('system' in actor) return 'The system';
  if ('asi' in actor) return 'PartnershipNet';
  return actor.name || 'Someone';
}

/** The change's parts in words (packages, seats, domains, admins, status, managed by). */
export function changePartsText(
  parts: SetupChange['parts'],
  packageName: (key: string) => string = (k) => k,
): string[] {
  const p = (parts ?? {}) as Partial<ChangeInput>;
  const out: string[] = [];
  if (Array.isArray(p.packages))
    out.push(
      p.packages.length > 0
        ? `Packages: ${p.packages.map(packageName).join(', ')}`
        : 'Packages: none',
    );
  if ('seat_limit' in p)
    out.push(
      p.seat_limit === null || p.seat_limit === undefined
        ? 'Seats: unlimited'
        : `Seats: ${p.seat_limit}`,
    );
  if (Array.isArray(p.domains_add) && p.domains_add.length > 0)
    out.push(`Add domains: ${p.domains_add.join(', ')}`);
  if (Array.isArray(p.domains_remove) && p.domains_remove.length > 0)
    out.push(`Remove domains: ${p.domains_remove.join(', ')}`);
  if (Array.isArray(p.admins) && p.admins.length > 0)
    out.push(`Administrators: ${p.admins.map((a) => a.email).join(', ')}`);
  if (p.client_status)
    out.push(
      `Client status: ${p.client_status}${p.trial_ends_at ? ` until ${dateOnly(p.trial_ends_at)}` : ''}`,
    );
  if (p.access_managed_by)
    out.push(`Access managed by: ${MANAGED_BY_LABEL[p.access_managed_by] ?? p.access_managed_by}`);
  return out;
}

/** May the caller take this decision on a change? (the build's `may` list or the contract's booleans) */
export function mayOnChange(c: SetupChange, action: 'approve' | 'reject' | 'cancel'): boolean {
  if (Array.isArray(c.may)) return c.may.includes(action);
  if (action === 'approve') return c.may_approve === true;
  if (action === 'reject') return c.may_reject === true;
  return c.may_cancel === true;
}

/** A Super Admin's own step, applied at once and recorded as approved by policy (owner 2026-10-15). */
export const POLICY_APPROVAL_TEXT = 'Approved by policy';

export const isPolicyApproval = (v: unknown): boolean => v === 'policy';

/** Access history actions in words; unknown ones are written out from their code. */
export const ACTION_LABEL: Record<string, string> = {
  database_insert: 'Created',
  database_update: 'Changed',
  database_delete: 'Removed',
  insert: 'Created',
  create: 'Created',
  created: 'Created',
  update: 'Changed',
  updated: 'Changed',
  delete: 'Removed',
  deleted: 'Removed',
};

export function actionText(action: string): string {
  const key = String(action ?? '').trim();
  const bare = key.replace(/^tenant_/, '');
  if (ACTION_LABEL[bare]) return ACTION_LABEL[bare];
  const words = bare.replace(/[._]+/g, ' ').trim();
  return words ? words.charAt(0).toUpperCase() + words.slice(1) : 'Change';
}

/** A before / after value in words ("—" for nothing). */
export function valueText(v: unknown): string {
  if (v === null || v === undefined || v === '') return '—';
  if (typeof v === 'boolean') return v ? 'yes' : 'no';
  if (typeof v === 'object') return JSON.stringify(v);
  return String(v);
}

/** The membership's end in words: "until 2027-06-01", "no end date". */
export const untilText = (validUntil: string | null | undefined): string =>
  validUntil ? `until ${dateText(validUntil)}` : 'no end date';

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

/** The date of a 'YYYY-MM-DD[ HH:MM:SS]' answer as parts, or null. */
function dateParts(s: string | null | undefined): { y: number; m: number; d: number } | null {
  const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(s ?? ''));
  if (!match) return null;
  const [y, m, d] = [Number(match[1]), Number(match[2]), Number(match[3])];
  return m >= 1 && m <= 12 ? { y, m, d } : null;
}

/** "23 Oct 2026" (the Manage users pages, owner 2026-10-16); '' without a date. */
export function dateText(s: string | null | undefined): string {
  const p = dateParts(s);
  return p ? `${p.d} ${MONTHS[p.m - 1]} ${p.y}` : '';
}

/** "1 Nov" in the current year, else "1 Nov 2027" (a review date next to an end date). */
export function shortDateText(s: string | null | undefined, now: Date = new Date()): string {
  const p = dateParts(s);
  if (!p) return '';
  return p.y === now.getUTCFullYear() ? `${p.d} ${MONTHS[p.m - 1]}` : dateText(s);
}

/** "Anna Example · anna@agency.com" (a guest seen by the host: the name only). */
export const personText = (r: Pick<MembershipRecord, 'person'>): string =>
  [r.person.name, r.person.email].filter((x) => x && String(x).trim() !== '').join(' · ');

/** The access of a membership in words. */
export const accessText = (r: MembershipRecord): string =>
  r.scope_summary ??
  r.scope?.summary ??
  (Array.isArray(r.scope?.rules) && r.scope.rules.length > 0
    ? `${r.scope.rules.length} combination${r.scope.rules.length === 1 ? '' : 's'}`
    : 'The whole organisation');
